import React from 'react'
import { Link } from 'react-router-dom'
import { ShieldCheckIcon, EyeSlashIcon, CubeIcon, CubeTransparentIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'

const FEATURED_PUNK_IDS = [3, 42, 99, 256, 512, 777]

export function HomePage() {
	return (
		<div className="bg-white">
			{/* Hero section */}
			<div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-zcash-900 text-white">
				<div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
						<div>
							<div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zcash-200 mb-4">
								<EyeSlashIcon className="h-4 w-4" />
								Drop #1 Live · 1024 Privacy Punks
							</div>
							<h1 className="text-4xl sm:text-6xl font-bold tracking-tight leading-tight">
								Privacy-first NFTs.<br />
								<span className="text-zcash-300">Censored on purpose.</span>
							</h1>
							<p className="mt-6 max-w-xl text-lg text-slate-300">
								Secresea is a Zcash-native marketplace for Non-Fungible Privacy
								Tokens (NFPTs) — the reference implementation of{' '}
								<Link
									to="/docs/standard"
									className="text-zcash-300 underline decoration-zcash-500/50 underline-offset-2 hover:text-white"
								>
									ZINC
								</Link>
								, an open Zcash inscription standard. Mint, trade and collect with
								shielded transactions, IPFS-pinned metadata and no public addresses
								leaking from your collection page.
							</p>
							<div className="mt-8 flex flex-wrap items-center gap-4">
								<Link
									to="/privacy-punks"
									className="rounded-md bg-zcash-500 hover:bg-zcash-400 text-white px-5 py-3 text-sm font-semibold shadow-md"
								>
									Mint a Privacy Punk
								</Link>
								<Link
									to="/collections"
									className="rounded-md border border-white/30 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10"
								>
									Explore Collections
								</Link>
								<Link to="/create" className="text-sm font-semibold leading-6 text-zcash-200 hover:text-white">
									Create your own NFPT <span aria-hidden="true">→</span>
								</Link>
								<Link to="/docs/standard" className="text-sm font-semibold leading-6 text-zcash-200 hover:text-white">
									Read the ZINC standard <span aria-hidden="true">→</span>
								</Link>
							</div>
						</div>
						<div className="grid grid-cols-3 gap-3">
							{FEATURED_PUNK_IDS.map((id) => (
								<Link
									key={id}
									to="/privacy-punks"
									className="block rounded-lg overflow-hidden bg-slate-800 border border-white/5 shadow-lg hover:scale-[1.02] transition-transform"
								>
									<img
										src={`/punks/images/${String(id).padStart(4, '0')}.png`}
										alt={`Privacy Punk #${id}`}
										loading="lazy"
										width={480}
										height={480}
										className="w-full aspect-square object-cover"
										style={{ imageRendering: 'pixelated' }}
									/>
								</Link>
							))}
						</div>
					</div>
				</div>
			</div>

			{/* ZINC standard — promo band */}
			<div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-zcash-900 text-white border-t border-white/5">
				<div className="mx-auto max-w-7xl px-6 py-20 sm:py-24 lg:px-8">
					<div className="mx-auto max-w-2xl text-center">
						<div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zcash-200 mb-4">
							<CubeTransparentIcon className="h-4 w-4" />
							An open standard, not a walled garden
						</div>
						<h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
							Secresea runs on <span className="text-zcash-300">ZINC</span>
						</h2>
						<p className="mt-5 text-lg leading-8 text-slate-300">
							<strong className="text-white">ZINC</strong> (Zcash INsCriptions) is an
							open, application-layer standard for inscribing typed,
							content-addressed data into Zcash shielded memos. It needs{' '}
							<strong className="text-white">no consensus change</strong> — it runs
							on the protocol deployed today, in any memo-capable wallet. An NFPT is
							simply its NFT profile, and Secresea is its reference implementation.
						</p>
					</div>

					<div className="mx-auto mt-14 grid max-w-5xl grid-cols-1 gap-6 sm:grid-cols-3">
						<div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur">
							<div className="text-xs font-mono uppercase tracking-widest text-zcash-300">ZINC-1</div>
							<h3 className="mt-2 text-lg font-semibold text-white">Inscription envelope</h3>
							<p className="mt-2 text-sm leading-6 text-slate-300">
								The shared memo format: a tiny, human-readable{' '}
								<code className="text-zcash-200">t:</code>type plus key:value records
								in ≤ 512 bytes. Write the parser once; reuse it everywhere.
							</p>
						</div>
						<div className="rounded-2xl border border-zcash-400/30 bg-zcash-500/10 p-6 backdrop-blur">
							<div className="text-xs font-mono uppercase tracking-widest text-zcash-200">ZINC-2 · NFPT</div>
							<h3 className="mt-2 text-lg font-semibold text-white">Non-fungible tokens</h3>
							<p className="mt-2 text-sm leading-6 text-slate-300">
								The Zcash-native answer to ERC-721 — but the owner, price and metadata
								pointer are shielded, and the mint itself is private. This is what
								every Secresea drop is.
							</p>
						</div>
						<div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur">
							<div className="text-xs font-mono uppercase tracking-widest text-zcash-300">ZINC-3 · ZONES</div>
							<h3 className="mt-2 text-lg font-semibold text-white">Shielded DNS zones</h3>
							<p className="mt-2 text-sm leading-6 text-slate-300">
								Complete DNS zones anchored to shielded inscriptions — private,
								censorship-resistant resolution for real domains. The same envelope
								also carries messages, files and attestations.
							</p>
						</div>
					</div>

					<div className="mt-12 flex flex-wrap items-center justify-center gap-4">
						<Link
							to="/docs/standard"
							className="rounded-md bg-zcash-500 hover:bg-zcash-400 text-white px-5 py-3 text-sm font-semibold shadow-md"
						>
							Read the ZINC standard
						</Link>
						<Link
							to="/docs/zinc-vs-zsa"
							className="rounded-md border border-white/30 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10"
						>
							ZINC vs ZSA
						</Link>
						<a
							href="https://github.com/FungeLLC/zinc"
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-1 text-sm font-semibold text-zcash-200 hover:text-white"
						>
							Library on GitHub <ArrowTopRightOnSquareIcon className="h-4 w-4" />
						</a>
						<a
							href="https://github.com/FungeLLC/zinc-scanner"
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-1 text-sm font-semibold text-zcash-200 hover:text-white"
						>
							Run the scanner <ArrowTopRightOnSquareIcon className="h-4 w-4" />
						</a>
					</div>
				</div>
			</div>

			{/* Features section */}
			<div className="bg-gray-50 py-24 sm:py-32">
				<div className="mx-auto max-w-7xl px-6 lg:px-8">
					<div className="mx-auto max-w-2xl lg:text-center">
						<h2 className="text-base font-semibold leading-7 text-zcash-600">Privacy by Design</h2>
						<p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
							Everything you need for private NFT trading
						</p>
						<p className="mt-6 text-lg leading-8 text-gray-600">
							Built on Zcash's proven zero-knowledge technology, our platform ensures complete transaction privacy while maintaining the benefits of blockchain transparency.
						</p>
					</div>
					<div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
						<dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
							<div className="flex flex-col">
								<dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
									<ShieldCheckIcon className="h-5 w-5 flex-none text-zcash-600" aria-hidden="true" />
									Shielded Transactions
								</dt>
								<dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
									<p className="flex-auto">All NFPT transactions use Zcash's shielded addresses, protecting both sender and recipient privacy while maintaining verifiable ownership.</p>
								</dd>
							</div>
							<div className="flex flex-col">
								<dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
									<EyeSlashIcon className="h-5 w-5 flex-none text-zcash-600" aria-hidden="true" />
									Private Collections
								</dt>
								<dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
									<p className="flex-auto">Create private collections with encrypted metadata, allowing selective disclosure of ownership and trading history to chosen parties.</p>
								</dd>
							</div>
							<div className="flex flex-col">
								<dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
									<CubeIcon className="h-5 w-5 flex-none text-zcash-600" aria-hidden="true" />
									Auto-Purchase Rules
								</dt>
								<dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
									<p className="flex-auto">Set up automated purchasing rules based on price, collection, and rarity criteria to never miss out on desired NFPTs.</p>
								</dd>
							</div>
						</dl>
					</div>
				</div>
			</div>

			{/* Stats section */}
			<div className="bg-white py-24 sm:py-32">
				<div className="mx-auto max-w-7xl px-6 lg:px-8">
					<div className="mx-auto max-w-2xl lg:max-w-none">
						<div className="text-center">
							<h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
								Built on Zcash, served from your wallet
							</h2>
							<p className="mt-4 text-lg leading-8 text-gray-600">
								Every Privacy Punk is a real Zcash inscription with IPFS-pinned metadata.
							</p>
						</div>
						<dl className="mt-16 grid grid-cols-1 gap-0.5 overflow-hidden rounded-2xl text-center sm:grid-cols-2 lg:grid-cols-4">
							<div className="flex flex-col bg-gray-400/5 p-8">
								<dt className="text-sm font-semibold leading-6 text-gray-600">Genesis Collection</dt>
								<dd className="order-first text-3xl font-semibold tracking-tight text-gray-900">1,024 Punks</dd>
							</div>
							<div className="flex flex-col bg-gray-400/5 p-8">
								<dt className="text-sm font-semibold leading-6 text-gray-600">Mint Memo Size</dt>
								<dd className="order-first text-3xl font-semibold tracking-tight text-gray-900">&lt; 512 B</dd>
							</div>
							<div className="flex flex-col bg-gray-400/5 p-8">
								<dt className="text-sm font-semibold leading-6 text-gray-600">Storage</dt>
								<dd className="order-first text-3xl font-semibold tracking-tight text-gray-900">IPFS · Pinata</dd>
							</div>
							<div className="flex flex-col bg-gray-400/5 p-8">
								<dt className="text-sm font-semibold leading-6 text-gray-600">Privacy</dt>
								<dd className="order-first text-3xl font-semibold tracking-tight text-gray-900">100% Shielded</dd>
							</div>
						</dl>
					</div>
				</div>
			</div>
		</div>
	)
} 